// =============================================================================
// Wizard Stepper — Shopify-style step progress indicator with responsive design
// =============================================================================

const STEPS = [
  { number: 1, label: 'Product' },
  { number: 2, label: 'Variant' },
  { number: 3, label: 'Quantity' },
  { number: 4, label: 'Review' },
];

interface WizardStepperProps {
  currentStep: number;
}

function StepCircle({ step, isActive, isCompleted }: { step: number; isActive: boolean; isCompleted: boolean }) {
  return (
    <span
      className={`
        flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold
        transition-all duration-300
        ${
          isCompleted
            ? 'bg-brand-600 text-white'
            : isActive
              ? 'bg-brand-600 text-white ring-[3px] ring-brand-600/20'
              : 'bg-surface-tertiary text-text-tertiary'
        }
      `}
    >
      {isCompleted ? (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        step
      )}
    </span>
  );
}

function Connector({ filled }: { filled: boolean }) {
  return (
    <div className="mx-2 h-[2px] flex-1 rounded-full bg-border sm:mx-3">
      <div
        className="h-full rounded-full bg-brand-600 transition-all duration-500"
        style={{ width: filled ? '100%' : '0%' }}
      />
    </div>
  );
}

export function WizardStepper({ currentStep }: WizardStepperProps) {
  return (
    <nav aria-label="Order creation progress" className="w-full">
      {/* Circles + connectors row */}
      <ol className="flex items-center">
        {STEPS.map((step, idx) => {
          const isActive = step.number === currentStep;
          const isCompleted = step.number < currentStep;
          const isLast = idx === STEPS.length - 1;

          return (
            <li key={step.number} className={`flex items-center ${isLast ? '' : 'flex-1'}`}>
              <StepCircle step={step.number} isActive={isActive} isCompleted={isCompleted} />
              {!isLast && <Connector filled={isCompleted} />}
            </li>
          );
        })}
      </ol>

      {/* Labels row — aligned under each circle */}
      <div className="mt-2 flex items-start">
        {STEPS.map((step, idx) => {
          const isActive = step.number === currentStep;
          const isCompleted = step.number < currentStep;
          const isLast = idx === STEPS.length - 1;

          return (
            <div key={step.number} className={`flex items-start ${isLast ? '' : 'flex-1'}`}>
              <span
                className={`
                  w-8 text-center text-[11px] leading-tight font-medium sm:text-xs
                  ${isActive ? 'text-brand-600 font-semibold' : isCompleted ? 'text-text-primary' : 'text-text-tertiary'}
                `}
              >
                {step.label}
              </span>
              {!isLast && <div className="mx-2 flex-1 sm:mx-3" />}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
